import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Style.css';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type NumberSelectProps = {
  questionid: string;
  maxlimit: string;
  minlimit: string;
  trailingText: string;
  disabled?: boolean;
  onSetValue: (value: { id: string; value: number }) => void;
  assessmentValues: Record<string, unknown>;
  startVal?: string;
  controlsInfo: string;
};

export function NumberSelect({
  questionid,
  maxlimit,
  minlimit,
  trailingText,
  disabled,
  onSetValue,
  assessmentValues,
  startVal,
  controlsInfo,
}: NumberSelectProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('0');

  function saveValue(newValue: number) {
    onSetValue({ id: questionid, value: newValue });
  }

  function setIncreaseNumber() {
    if (disabled) return;
    let newValue = Number(inputValue);
    if (newValue < Number(maxlimit)) {
      newValue++;
      setInputValue(newValue.toString());
      saveValue(newValue);
    }
  }

  function setDecreaseNumber() {
    if (disabled) return;
    let newValue = Number(inputValue);
    if (newValue > 0) {
      newValue--;
      setInputValue(newValue.toString());
      saveValue(newValue);
    }
  }

  function numberChangeHandler(event) {
    const numericValue = Number(event.target.value);
    setInputValue(event.target.value);
    saveValue(numericValue);
  }

  const handleFocusOut = (event) => {
    if (maxlimit && Number(event.target.value) > Number(maxlimit)) {
      setInputValue(maxlimit);
      saveValue(Number(maxlimit));
    } else if (minlimit && (Number(event.target.value) < Number(minlimit) || !event.target.value)) {
      setInputValue(minlimit);
      saveValue(Number(minlimit));
    }
  };

  const currentVal = assessmentValues[questionid];

  useEffect(() => {
    if (currentVal != null) {
      setInputValue(String(currentVal));
    } else if (startVal) {
      setInputValue(startVal);
      saveValue(Number(startVal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionid, startVal, currentVal]);

  return (
    <div className={`number_field number_field_numberSelect ${disabled ? 'disabled' : 'active'}`}>
      <div className="number_wrapper">
        <div className="number_container fs-exclude">
          <input
            type="number"
            className="number"
            id={questionid}
            onChange={numberChangeHandler}
            onBlur={handleFocusOut}
            value={inputValue}
            max={maxlimit}
            min="0"
            disabled={disabled}
          />
          {trailingText && <label htmlFor={questionid}>{trailingText}</label>}
        </div>
        <div className="number_controls">
          <button
            type="button"
            className="up_btn"
            onClick={setIncreaseNumber}
            aria-labelledby={`${questionid}_up`}
            disabled={disabled}
          >
            <span className="visuallyHidden" id={`${questionid}_up`}>
              {t('A134')} {controlsInfo}
            </span>
            <FontAwesomeIcon icon={faChevronUp} className="icon" />
          </button>
          <button
            type="button"
            className="down_btn"
            onClick={setDecreaseNumber}
            aria-labelledby={`${questionid}_down`}
            disabled={disabled}
          >
            <span className="visuallyHidden" id={`${questionid}_down`}>
              {t('A135')} {controlsInfo}
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="icon" />
          </button>
        </div>
      </div>
    </div>
  );
}
